import { type ReactNode, createContext, useContext, useState } from "react";
import invariant from "tiny-invariant";

export interface SgidContextType {
  localSgidToObjectMap: Record<string, unknown>;
  onLocalSgidToObjectMapChange: ({
    sgid,
    object,
  }: {
    sgid: string;
    object: unknown;
  }) => void;
}

const SgidContext = createContext<SgidContextType | null>(null);
SgidContext.displayName = "SgidContext";

export const useSgidContext = () => {
  const context = useContext(SgidContext);
  invariant(context, "useSgidContext must be used within a SgidProvider");
  return context;
};

export function SgIdProvider({
  children,
  initialSgidToObjectMap,
}: {
  children: ReactNode;
  initialSgidToObjectMap?: Record<string, unknown>;
}) {
  const [localSgidToObjectMap, setLocalSgidToObjectMap] = useState<
    Record<string, unknown>
  >(initialSgidToObjectMap ?? {});

  const onLocalSgidToObjectMapChange: SgidContextType["onLocalSgidToObjectMapChange"] =
    ({ sgid, object }) => {
      setLocalSgidToObjectMap(prev => ({
        ...prev,
        [sgid]: object,
      }));
    };

  return (
    <SgidContext.Provider
      value={{
        localSgidToObjectMap,
        onLocalSgidToObjectMapChange,
      }}
    >
      {children}
    </SgidContext.Provider>
  );
}
